import { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useAppHeaderConfig } from "@/components/app-header/AppHeaderContext";
import { useGetUserDetails } from "@/hooks/useGetUserDetails";

import { useAppTabsStore } from "../context/appTabsStore";
import { ONBOARDING_TAB_ID, tabForPath } from "../tabRoutes";

/**
 * The URL drives the active tab: load the user's tabs once their uid is
 * known, then visit the location's tab on every navigation; leaving
 * onboarding drops its Get Started tab, which can't be closed. A page's own
 * breadcrumb names its page tab ("Agents / #doe-jane" → "#doe-jane").
 */
export function useTabRouteSync(): void {
  const { pathname, search } = useLocation();
  const {
    userDetails: { id: uid }
  } = useGetUserDetails();
  const hydrate = useAppTabsStore((s) => s.hydrate);
  const visit = useAppTabsStore((s) => s.visit);
  const close = useAppTabsStore((s) => s.close);
  const setMeta = useAppTabsStore((s) => s.setMeta);
  const header = useAppHeaderConfig();

  useEffect(() => {
    if (uid) hydrate(uid);
  }, [uid, hydrate]);

  const target = tabForPath(pathname, search);
  const storeUid = useAppTabsStore((s) => s.uid);
  useEffect(() => {
    visit(target);
    if (
      target.id !== ONBOARDING_TAB_ID &&
      useAppTabsStore.getState().tabs.some((t) => t.id === ONBOARDING_TAB_ID)
    )
      close(ONBOARDING_TAB_ID);
    // `target` is derived from these; a new object each render must not re-visit.
  }, [pathname, search, storeUid, visit, close]);

  const crumbs = header?.crumbs;
  const last = crumbs?.[crumbs.length - 1]?.label;
  const pageTitle =
    target.kind === "page"
      ? typeof last === "string"
        ? last
        : target.title
      : null;
  useEffect(() => {
    if (pageTitle) setMeta(target.id, { title: pageTitle });
  }, [pageTitle, target.id, setMeta]);
}
